import {Avatar, Button, Image, Input, message, Modal, Popconfirm, Tag, Tooltip} from 'antd'
import {
    course_getStudents_Api,
    course_rejectIdentity_Api,
    course_unblockIdentity_Api,
    course_verifyIdentity_Api,
} from 'apis'
import classNames from 'classnames'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useState} from 'react'
import {BiCheckCircle, BiInfoCircle, BiXCircle} from 'react-icons/bi'
import {MdOutlineBlock, MdOutlineRestartAlt} from 'react-icons/md'
import {PiStudentFill} from 'react-icons/pi'
import {RiShieldCheckFill} from 'react-icons/ri'
import usePaginationHelper, {createPagerKey,} from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault} from 'views/Shared'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import useAsyncAction from "../../../../../modules/asyncCache/useAsyncAction";
import {LayoutContext} from "../../../../../components/layouts/Default/LayoutContext";
import {formatSimplifyDate} from "../../../../../helpers/dateUtils";
import _ from "lodash";
import {IdentifierTypes} from "../../../../AppLocals/appFieldTypes";
import {GoDotFill} from "react-icons/go";
import getAvatar from "../../../../../helpers/getAvatar";
import {renderMaskImage} from "../../../../../components/renderFunctions/renderMaskImage";
import {getGroupColor} from "../../functions/renderCourseClassStudent";
import getAliasName from "../../../../../helpers/getAliasName";
import {
    getInitials,
    renderGenderBadge,
    renderStudentCardItem,
    renderStudentTableRow
} from "../../functions/renderStudent";
import {formatDate} from "../../../../../helpers/datetimeHelpers";
import {EstimatedDobTag} from "../../../../../components/common/EstimatedDobTag";
import copyToClipboard from "copy-to-clipboard";
import {IoCopyOutline} from "react-icons/io5";
import PagePaginationWrapper from "../../../../../modules/asyncCache/components/PagePagination/PagePaginationWrapper";
import {renderLoading} from "../../../../AppLocals/functions/renderers/renderLoading";
import EmptyHolder from "../../../../../components/EmptyHolder";


const IDENTITY_STATUS = {
    UNVERIFIED: 0,
    PENDING: -1,
    VERIFIED: 1,
    REJECTED: -2,
    BLOCKED: -3,
}

const STATUS_CONFIG = {
    [IDENTITY_STATUS.UNVERIFIED]: {label: 'identity unverified', color: 'default'},
    [IDENTITY_STATUS.PENDING]: {label: 'identity pending', color: 'orange'},
    [IDENTITY_STATUS.VERIFIED]: {label: 'identity verified', color: 'green'},
    [IDENTITY_STATUS.REJECTED]: {label: 'identity rejected', color: 'red'},
    [IDENTITY_STATUS.BLOCKED]: {label: 'identity blocked', color: 'purple'},
}

const STATUS_TABS = [
    {key: '', label: 'all statuses'},
    {key: '-1', label: 'identity pending'},
    {key: '0', label: 'identity unverified'},
    {key: '-2', label: 'identity rejected'},
    {key: '-3', label: 'identity blocked'},
    {key: '1', label: 'identity verified'},
]

const RejectModal = ({visible, student, onClose, onRejected}) => {
    const t = useTranslate()
    const [reason, setReason] = useState('')

    const {handleAsyncAction: doReject, isLoading} = useAsyncAction({
        apiInfo: course_rejectIdentity_Api,
        onSuccess: () => {
            message.info(t('identity rejected successfully'))
            setReason('')
            onRejected()
            onClose()
        },
    })

    const handleSubmit = useCallback(() => {
        if (!reason.trim()) {
            message.warning(t('please enter reject reason'))
            return
        }
        doReject({reason}, {id: student?.id})
    }, [student, reason])


    const initials = getInitials(student?.fullname)
    const color = getGroupColor(student?.fullname)
    const patronSaint = getAliasName(student, 'patron_saint')
    const isUnknownDob = !student?.date_of_birth

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            className="custom-modal"
            title={
                <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                    {t('reject identity')}
                </div>
            }
            footer={null}
            destroyOnHidden>
            <div className="flex flex-col gap-4 py-2">
                <div className="flex items-center gap-2">
                    {student?.avatar ? (
                        <Avatar
                            size={40}
                            src={getAvatar(student)}
                            className="rounded-full"
                        />
                    ) : (
                        <div
                            style={{width: 40, height: 40, backgroundColor: color, flexShrink: 0}}
                            className="rounded-full flex flex-center">
                            <span className="text-white font-bold text-xs leading-none">{initials}</span>
                        </div>
                    )}
                    <div className="flex flex-col flex-1">
                        {patronSaint && (
                            <span className="text-xs font-medium text-gray-600 leading-snug truncate">{patronSaint}</span>
                        )}
                        <div className="flex gap-3">
                            <span className="font-bold text-sm text-gray-700 tracking-wide">
                                {student?.fullname}
                            </span>
                            {student?.identifier_code && (
                                <div className="flex gap-0.5 text-xs">
                                    <span className="text-color-400 uppercase tracking-wider">
                                        {t(_.find(IdentifierTypes, {value: student.identifier_type})?.label) || t('identifier')}:
                                    </span>
                                    <span className="font-mono font-medium text-gray-700">{student.identifier_code}</span>
                                </div>
                            )}
                        </div>
                        {(student?.gender === 1 || student?.gender === 2 || !isUnknownDob) && (
                            <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                                {renderGenderBadge(student?.gender, t)}
                                {!isUnknownDob && (
                                    <span className="flex items-center gap-1 text-2xs text-color-300 whitespace-no-wrap">
                                        {formatDate(student?.date_of_birth, 'DD/MM/YYYY')}
                                        {student?.date_of_birth_is_estimated && <EstimatedDobTag/>}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <Input.TextArea
                    rows={3}
                    placeholder={t('reject reason')}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={500}
                    showCount
                />
                <div className="mt-3 flex justify-end gap-2">
                    <Button onClick={onClose}>{t('cancel')}</Button>
                    <Button type="primary" danger loading={isLoading} onClick={handleSubmit}>
                        {t('reject identity')}
                    </Button>
                </div>
            </div>
        </Modal>
    )
}

const VerifyDetailModal = ({student, visible, onClose, onVerified, onOpenReject}) => {
    const t = useTranslate()

    const {handleAsyncAction: doVerify, isLoading: verifying} = useAsyncAction({
        apiInfo: course_verifyIdentity_Api,
        onSuccess: () => {
            message.success(t('identity verified successfully'))
            onVerified()
            onClose()
        },
    })

    const {handleAsyncAction: doUnblock, isLoading: unblocking} = useAsyncAction({
        apiInfo: course_unblockIdentity_Api,
        onSuccess: () => {
            message.success(t('identity unblocked successfully'))
            onVerified()
            onClose()
        },
    })

    if (!student) return null

    const status = student.identity_status ?? IDENTITY_STATUS.UNVERIFIED
    const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG[IDENTITY_STATUS.UNVERIFIED]
    const isPending = status === IDENTITY_STATUS.PENDING
    const isBlocked = status === IDENTITY_STATUS.BLOCKED
    const isRejected = status === IDENTITY_STATUS.REJECTED
    const hasDocs = student.identity_document_front_url || student.identity_document_back_url

    const footer = [
        <Button key="close" onClick={onClose}>{t('close')}</Button>,
        isPending && (
            <Button
                key="reject"
                danger
                icon={<BiXCircle size={14}/>}
                onClick={() => {
                    onClose()
                    onOpenReject(student)
                }}>
                {t('reject identity')}
            </Button>
        ),
        isPending && (
            <Button
                key="verify"
                type="primary"
                icon={<BiCheckCircle size={14}/>}
                loading={verifying}
                onClick={() => doVerify({}, {id: student.id})}>
                {t('verify identity')}
            </Button>
        ),
        isBlocked && (
            <Popconfirm
                key="unblock"
                title={t('confirm send back to pending')}
                onConfirm={() => doUnblock({}, {id: student.id})}
                okText={t('confirm')}
                cancelText={t('cancel')}>
                <Button
                    type="primary"
                    icon={<MdOutlineRestartAlt size={14}/>}
                    loading={unblocking}>
                    {t('send back to pending')}
                </Button>
            </Popconfirm>
        ),
    ].filter(Boolean)

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            className="custom-modal"
            title={
                <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                    {t('detail information')}
                </div>
            }
            footer={footer}
            width={640}
            destroyOnHidden>
            <div className="flex flex-col gap-4 py-3">
                {/* Student info */}
                <div className="flex items-center gap-2 flex-wrap">
                    <Avatar
                        size={40}
                        icon={<PiStudentFill size={20} className="text-indigo-400"/>}
                        src={getAvatar(student)}
                        className="flex flex-center bg-secondary-50"
                    />
                    <div className="flex flex-col flex-1 ">
                        <span className="font-bold text-lg tracking-wide">
                            {student.fullname}
                        </span>
                        {student.student_idcode && (
                            <span className="text-xs font-mono text-color-400 font-normal">#{student.student_idcode}</span>
                        )}
                    </div>
                    <Tag style={{ marginInlineEnd: 0 }} color={cfg.color} className="text-xs">
                        {status === IDENTITY_STATUS.BLOCKED && <MdOutlineBlock size={11} className="inline mr-0.5"/>}
                        {status === IDENTITY_STATUS.VERIFIED &&
                        <RiShieldCheckFill size={11} className="inline mr-0.5"/>}
                        {t(cfg.label)}
                    </Tag>
                </div>

                {/* Reason if blocked or rejected */}
                {(isBlocked || isRejected) && student.identity_rejected_reason && (
                    <div className="p-3 flex gap-2 rounded-xl bg-red-50">
                        <MdOutlineBlock size={20} className="text-primary-400 flex-shrink-0"/>
                        <div className="flex flex-col gap-0.5">
                            <span className="font-bold text-sm text-primary-500 uppercase tracking-wide leading-snug">{t('reason')}</span>
                            <span className="text-primary-600 italic tracking-wider">{student.identity_rejected_reason}</span>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    {student.date_of_birth && (
                        <div className="flex flex-col gap-0.5">
                            <span className="font-medium text-xs text-color-400 uppercase tracking-wider">{t('date of birth')}</span>
                            <span className="font-medium text-gray-700">{formatSimplifyDate(student.date_of_birth, 'medium')}</span>
                        </div>
                    )}
                    {student.identifier_code && (
                        <div className="flex flex-col gap-0.5">
                            <span className="font-medium text-xs text-color-400 uppercase tracking-wider">
                                {t(_.find(IdentifierTypes, {value: student.identifier_type})?.label) || t('identifier')}
                            </span>
                            <span className="font-medium text-gray-700">{student.identifier_code}</span>
                        </div>
                    )}
                </div>
                {/* Document images */}
                <div className="flex flex-col gap-2">
                    <span className="font-medium text-xs text-color-400 uppercase tracking-wider">{t('identity documents')}</span>
                    {hasDocs ? (
                        <Image.PreviewGroup>
                            <div className="flex gap-4 flex-wrap">
                                {student.identity_document_front_url && (
                                    <div className="flex flex-col items-center gap-1.5">
                                        <span className="text-xs text-color-400">{t('identity document front')}</span>
                                        <Image
                                            src={student.identity_document_front_url}
                                            width={240}
                                            height={160}
                                            className="rounded-lg object-cover border border-color-100"
                                            alt={t('identity document front')}
                                            preview={{mask: renderMaskImage()}}
                                        />
                                    </div>
                                )}
                                {student.identity_document_back_url && (
                                    <div className="flex flex-col items-center gap-1.5">
                                        <span className="text-xs text-color-400">{t('identity document back')}</span>
                                        <Image
                                            src={student.identity_document_back_url}
                                            width={240}
                                            height={160}
                                            className="rounded-lg object-cover border border-color-100"
                                            alt={t('identity document back')}
                                            preview={{mask: renderMaskImage()}}
                                        />
                                    </div>
                                )}
                            </div>
                        </Image.PreviewGroup>
                    ) : (
                        <div className="flex items-center gap-2 p-3 rounded-lg background-50 border border-color-50">
                            <BiInfoCircle size={16} className="text-color-400"/>
                            <span className="text-sm text-color-400 italic">{t('no documents uploaded')}</span>
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    {(student.contact_phone || student.contact_email) && (
                        <div className="flex flex-col gap-0.5">
                            <span className="font-medium text-xs text-color-400 uppercase tracking-wider">{t('contact')}</span>
                            <span className="font-medium text-gray-700">
                                {student?.contact_phone}
                            </span>
                            <span className="font-medium text-gray-700">
                                {student?.contact_email}
                            </span>
                        </div>
                    )}
                    {student.contact_address && (
                        <div className="flex flex-col gap-0.5">
                            <span className="font-medium text-xs text-color-400 uppercase tracking-wider">
                                {t('address')}
                            </span>
                            <span className="font-medium text-gray-700">{student?.contact_address}</span>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}

const StudentIdentityRow = ({item, onVerified, onRejected, onViewDetail, t, gridColumn}) => {
    const {isLg} = useContext(LayoutContext)

    const {
        avatar, gender,
        fullname, date_of_birth, date_of_birth_is_estimated,
        student_idcode,
    } = item ?? {}

    const {handleAsyncAction: doVerify, isLoading: verifying} = useAsyncAction({
        apiInfo: course_verifyIdentity_Api,
        onSuccess: () => {
            message.success(t('identity verified successfully'))
            onVerified()
        },
    })

    const {handleAsyncAction: doUnblock, isLoading: unblocking} = useAsyncAction({
        apiInfo: course_unblockIdentity_Api,
        onSuccess: () => {
            message.success(t('identity unblocked successfully'))
            onVerified()
        },
    })

    const status = item.identity_status ?? IDENTITY_STATUS.UNVERIFIED
    const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG[IDENTITY_STATUS.UNVERIFIED]
    const isPending = status === IDENTITY_STATUS.PENDING
    const isVerified = status === IDENTITY_STATUS.VERIFIED
    const isBlocked = status === IDENTITY_STATUS.BLOCKED
    const hasDocs = item.identity_document_front_url || item.identity_document_back_url


    const isUnknownDob = !date_of_birth
    const initials = getInitials(fullname)
    const color = getGroupColor(fullname)
    const patronSaint = getAliasName(item, 'patron_saint')

    if (!item) return null

    return (
        <div className="flex gap-3 relative list-item">

            {/*  Mobile row  */}
            {!isLg && (
                <div className="px-2 py-3 w-full flex flex-col gap-2">
                    <div className="flex items-start gap-2">
                        {avatar ? (
                            <Avatar
                                size={36}
                                src={avatar}
                                className="rounded-full"
                            />
                        ) : (
                            <div
                                style={{width: 36, height: 36, backgroundColor: color, flexShrink: 0}}
                                className="rounded-full flex flex-center">
                                <span className="text-white font-bold text-xs leading-none">{initials}</span>
                            </div>
                        )}
                        <div className="flex flex-col flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                                {patronSaint && (
                                    <span className="text-xs font-medium text-gray-600 leading-snug truncate">
                                        {patronSaint}
                                    </span>
                                )}
                                <span className="font-semibold text-sm truncate">
                                    {item.fullname}
                                </span>
                                {isVerified && (
                                    <Tooltip title={t('identity verified')}>
                                        <RiShieldCheckFill size={14} className="text-green-500 flex-shrink-0"/>
                                    </Tooltip>
                                )}
                            </div>
                            <div className="mb-2 flex items-center justify-between gap-2 flex-wrap">
                                {item.student_idcode && (
                                    <span className="text-xs font-mono text-color-400">#{item.student_idcode}</span>
                                )}
                                <Tag style={{ marginInlineEnd: 0 }} variant="filled" color={cfg.color} className="text-xs">
                                    {isBlocked && <MdOutlineBlock size={11} className="inline mr-0.5"/>}
                                    {t(cfg.label)}
                                </Tag>
                            </div>
                            {item.identifier_code && (
                                <div className="flex items-baseline gap-1 text-xs">
                                    <GoDotFill size={10} className="text-color-500" />
                                    <span className="font-medium text-gray-600">
                                        {t(_.find(IdentifierTypes, {value: item?.identifier_type})?.label)}:
                                    </span>
                                    <span className="font-mono font-bold text-blue-500">
                                        {item?.identifier_code}
                                    </span>
                                </div>
                            )}
                            {hasDocs && (
                                <div className="flex gap-2">
                                    {item.identity_document_front_url && (
                                        <Image
                                            src={item.identity_document_front_url}
                                            width={60}
                                            height={40}
                                            className="rounded object-cover"
                                            alt={t('identity document front')}
                                            preview={{mask: renderMaskImage()}}
                                        />
                                    )}
                                    {item.identity_document_back_url && (
                                        <Image
                                            src={item.identity_document_back_url}
                                            width={60}
                                            height={40}
                                            className="rounded object-cover"
                                            alt={t('identity document back')}
                                            preview={{mask: renderMaskImage()}}
                                        />
                                    )}
                                </div>
                            )}
                            {isBlocked && item.identity_rejected_reason && (
                                <div className="text-xs text-purple-600 pl-10 italic">
                                    ⚠ {item.identity_rejected_reason}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="mt-2 flex justify-end flex-wrap gap-2">
                        <Button
                            size="small"
                            // color="default" variant="filled"
                            icon={<BiInfoCircle size={15}/>}
                            onClick={() => onViewDetail(item)}
                            className="flex flex-center bg-blue-100 rounded-md text-xs text-blue-500 no-border">
                            {t('view detail')}
                        </Button>
                        {isPending && (
                            <>
                                <Button
                                    size="small"
                                    color="primary" variant="solid"
                                    icon={<BiCheckCircle size={15}/>}
                                    loading={verifying}
                                    onClick={() => doVerify({}, {id: item.id})}
                                    className="text-xs rounded-md">
                                    {t('verify identity')}
                                </Button>
                                <Button
                                    size="small"
                                    color="primary" variant="filled"
                                    icon={<BiXCircle size={15}/>}
                                    onClick={() => onRejected(item)}
                                    className="text-xs rounded-md">
                                    {t('reject')}
                                </Button>
                            </>
                        )}
                        {isBlocked && (
                            <Popconfirm
                                title={t('confirm send back to pending')}
                                onConfirm={() => doUnblock({}, {id: item.id})}
                                okText={t('confirm')}
                                cancelText={t('cancel')}>
                                <Button
                                    size="small"
                                    color="primary" variant="solid"
                                    icon={<MdOutlineRestartAlt size={15}/>}
                                    loading={unblocking}
                                    className="text-xs rounded-md">
                                    {t('send back to pending')}
                                </Button>
                            </Popconfirm>
                        )}
                    </div>
                </div>
            )}

            {/*  Web row  */}
            {isLg && (
                <div style={{gridTemplateColumns: gridColumn}} className="p-1 w-full grid items-center">
                    <div className="p-2 flex items-start gap-2 min-w-0">
                        {avatar ? (
                            <Avatar
                                size={36}
                                src={avatar}
                                className="rounded-full"
                            />
                        ) : (
                            <div
                                style={{width: 36, height: 36, backgroundColor: color, flexShrink: 0}}
                                className="rounded-full flex flex-center">
                                <span className="text-white font-bold text-xs leading-none">{initials}</span>
                            </div>
                        )}
                        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                            {patronSaint && (
                                <span className="text-xs font-medium text-gray-600 leading-snug truncate">{patronSaint}</span>
                            )}
                            <div className="flex items-center gap-1.5 leading-tight">
                                <span className="font-semibold text-sm truncate">
                                    {item?.fullname}
                                </span>
                                {isVerified && (
                                    <Tooltip title={t('identity verified')}>
                                        <RiShieldCheckFill size={13} className="text-green-500 flex-shrink-0"/>
                                    </Tooltip>
                                )}
                            </div>
                            {(gender === 1 || gender === 2 || !isUnknownDob) && (
                                <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                                    {renderGenderBadge(gender, t)}
                                    {!isUnknownDob && (
                                        <span className="flex items-center gap-1 text-2xs text-color-300 whitespace-no-wrap">
                                        {formatDate(date_of_birth, 'DD/MM/YYYY')}
                                            {date_of_birth_is_estimated && <EstimatedDobTag/>}
                                    </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="p-2 text-center font-medium text-xs text-color-200">
                        {student_idcode && (
                            <span
                                onClick={() => { copyToClipboard(student_idcode); message.info(t('copied')) }}
                                className="text-blue-500 hover:text-blue-700 cursor-pointer flex flex-center gap-1 whitespace-no-wrap">
                                #{student_idcode}
                                <IoCopyOutline size={9}/>
                            </span>
                        )}
                    </div>
                    <div className="p-2 text-center flex flex-col gap-0.5">
                        {item?.identifier_code ? (
                            <div className="flex flex-col">
                                <span className="font-medium text-2xs text-gray-500 tracking-wider">
                                    {t(_.find(IdentifierTypes, {value: item?.identifier_type})?.label)}
                                </span>
                                <span className="font-mono font-bold text-gray-600">
                                    {item?.identifier_code}
                                </span>
                            </div>
                        ) : (
                            <span className="text-xs text-color-300 italic">—</span>
                        )}
                    </div>
                    <div className="p-2 text-center flex flex-center">
                        {hasDocs ? (
                            <div className="flex gap-1.5">
                                {item?.identity_document_front_url && (
                                    <Image
                                        src={item?.identity_document_front_url}
                                        width={44}
                                        height={30}
                                        className="rounded object-cover"
                                        alt={t('identity document front')}
                                        preview={{mask: renderMaskImage()}}
                                    />
                                )}
                                {item?.identity_document_back_url && (
                                    <Image
                                        src={item?.identity_document_back_url}
                                        width={44}
                                        height={30}
                                        className="rounded object-cover"
                                        alt={t('identity document back')}
                                        preview={{mask: renderMaskImage()}}
                                    />
                                )}
                            </div>
                        ) : (
                            <span className="text-xs text-color-300 italic">
                                {'—'}
                            </span>
                        )}
                    </div>
                    <div className="p-2 mx-auto flex items-center gap-2">
                        <Tag style={{ marginInlineEnd: 0 }} variant="filled" color={cfg.color} className="mr-0 text-xs whitespace-nowrap">
                            {isBlocked && <MdOutlineBlock size={11} className="inline mr-1"/>}
                            {isVerified && <RiShieldCheckFill size={11} className="inline mr-1"/>}
                            {t(cfg.label)}
                        </Tag>
                    </div>
                    <div className="p-2 flex flex-center gap-1.5">
                        <Tooltip title={t('view detail')}>
                            <Button
                                size="small"
                                // color="default" variant="filled"
                                icon={<BiInfoCircle size={14}/>}
                                onClick={() => onViewDetail(item)}
                                className="flex flex-center bg-blue-100 rounded-md text-blue-500 no-border"
                            />
                        </Tooltip>
                        {isPending && (
                            <>
                                <Tooltip title={t('verify identity')}>
                                    <Button
                                        size="small" color="primary" variant="solid"
                                        icon={<BiCheckCircle size={14}/>}
                                        loading={verifying}
                                        onClick={() => doVerify({}, {id: item.id})}
                                        className="flex flex-center rounded-md no-border"
                                    />
                                </Tooltip>
                                <Tooltip title={t('reject identity')}>
                                    <Button
                                        size="small" color="primary" variant="filled"
                                        icon={<BiXCircle size={14}/>}
                                        onClick={() => onRejected(item)}
                                        className="flex flex-center rounded-md"
                                    />
                                </Tooltip>
                            </>
                        )}
                        {isBlocked && (
                            <Tooltip title={t('send back to pending')}>
                                <Popconfirm
                                    title={t('confirm send back to pending')}
                                    onConfirm={() => doUnblock({}, {id: item.id})}
                                    okText={t('confirm')}
                                    cancelText={t('cancel')}>
                                    <Button
                                        size="small" color="primary" variant="solid"
                                        icon={<MdOutlineRestartAlt size={14}/>}
                                        loading={unblocking}
                                        className="flex flex-center rounded-md no-border"
                                    />
                                </Popconfirm>
                            </Tooltip>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

const IdentityResolution = ({owner_id, owner_type}) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const [activeStatus, setActiveStatus] = useState('')
    const [rejectTarget, setRejectTarget] = useState(null)
    const [detailTarget, setDetailTarget] = useState(null)
    const [refreshKey, setRefreshKey] = useState(0)

    const refresh = useCallback(() => setRefreshKey((k) => k + 1), [])

    const GRID_COLUMNS = '1fr 120px 120px 120px 140px 100px';

    const renderList = useCallback(
        ({keyword, sortBy: currentSort, filterBy, refreshKey}) => (
            <PagePaginationWrapper
                apiInfo={course_getStudents_Api}
                query={bindQueryParams([{id: owner_id}, {type: owner_type}])}
                // values={{...(activeStatus !== '' ? {identity_status: activeStatus} : {}), keyword, currentSort}}
                values={{
                    identity_status: activeStatus,
                    keyword, currentSort,
                }}
                refreshKey={refreshKey}
                renderItem={(item) => {
                    if (!item) return null
                    return (
                        <StudentIdentityRow
                            key={item.id}
                            item={item}
                            onVerified={refresh}
                            onRejected={setRejectTarget}
                            onViewDetail={setDetailTarget}
                            t={t}
                            gridColumn={GRID_COLUMNS}
                        />
                    )
                }}
                renderLoading={() => renderLoading()}
                renderEmpty={() => <EmptyHolder/>}
                Wrapper={({children}) => (
                    <div className="flex flex-col divide-y divide-gray-200 list-item-workspace">
                        {isLg && (
                            <div
                                style={{gridTemplateColumns: GRID_COLUMNS}}
                                className="px-1 grid text-center font-semibold text-xs text-gray-500 tracking-wider uppercase">
                                <span className="p-2 my-auto">{t('student')}</span>
                                <span className="p-2 my-auto">{t('student code')}</span>
                                <span className="p-2 my-auto">{t('identifier')}</span>
                                <span className="p-2 my-auto">{t('documents')}</span>
                                <span className="p-2 my-auto">{t('status')}</span>
                                <span className="p-2 my-auto">{t('actions')}</span>
                            </div>
                        )}
                        {children}
                    </div>
                )}
            />
        ),
        [owner_id, owner_type, t, activeStatus]
    )

    const [pager_widget] = usePaginationHelper({
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        renderList,
        // pagerInfo: {
        //     RenderEntity: React.Fragment,
        //     PaginationMeta: NullAsDefault,
        //     apiInfo: course_getStudents_Api,
        //     query: bindQueryParams([{id: owner_id}, {type: owner_type}]),
        //     renderItem: (item) => {
        //         if (!item) return null
        //         return (
        //             <StudentIdentityRow
        //                 key={item.id}
        //                 item={item}
        //                 onVerified={refresh}
        //                 onRejected={setRejectTarget}
        //                 onViewDetail={setDetailTarget}
        //                 t={t}
        //                 gridColumn={GRID_COLUMNS}
        //             />
        //         )
        //     },
        //     renderWrap: (child) => (
        //         <div className="flex flex-col divide-y divide-gray-200 list-item-workspace">
        //             {isLg && (
        //                 <div
        //                     style={{gridTemplateColumns: GRID_COLUMNS}}
        //                     className="px-1 grid text-center font-semibold text-xs text-gray-500 tracking-wider uppercase">
        //                     <span className="p-2 my-auto">{t('student')}</span>
        //                     <span className="p-2 my-auto">{t('student code')}</span>
        //                     <span className="p-2 my-auto">{t('identifier')}</span>
        //                     <span className="p-2 my-auto">{t('documents')}</span>
        //                     <span className="p-2 my-auto">{t('status')}</span>
        //                     <span className="p-2 my-auto">{t('actions')}</span>
        //                 </div>
        //             )}
        //             {child}
        //         </div>
        //     ),
        // },
        // withValues: (values) => ({
        //     ...values,
        //     ...(activeStatus !== '' ? {identity_status: activeStatus} : {}),
        // }),
        filter_params: {owner_id, owner_type},
        dependencies: [owner_id, owner_type, activeStatus, refreshKey],
        withKey: createPagerKey([owner_id, owner_type, activeStatus, refreshKey]),
        renderHeader: ([searchBar, filterBar]) => (
            <div className="flex flex-col gap-3">
                {filterBar}
                {searchBar}
            </div>
        ),
    })

    return (
        <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="flex flex-col flex-1">
                <h2 className="font-bold text-lg text-gray-800 leading-tight">
                    {t('identity resolution')}
                </h2>
              <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
            </div>

            {/* Status filter tabs */}
            <div className="flex flex-wrap gap-2">
                {STATUS_TABS.map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveStatus(tab.key)}
                        className={classNames(
                            'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all',
                            activeStatus === tab.key
                                ? 'bg-secondary-500 text-white'
                                : 'background hover:bg-secondary-50 shadow-sm hover:shadow-md text-color-200 hover:text-secondary-600'
                        )}>
                        {t(tab.label)}
                    </button>
                ))}
            </div>
            {pager_widget}
            <div className="h-16"/>
            <RejectModal
                visible={!!rejectTarget}
                student={rejectTarget}
                onClose={() => setRejectTarget(null)}
                onRejected={refresh}
            />
            <VerifyDetailModal
                visible={!!detailTarget}
                student={detailTarget}
                onClose={() => setDetailTarget(null)}
                onVerified={refresh}
                onOpenReject={(student) => {
                    setRejectTarget(student)
                }}
            />
        </div>
    )
}

export default IdentityResolution
